import React from 'react';
import { format } from 'date-fns';
import {
  actorLabelClasses,
  directionGlyphClasses,
  eventTimestampClasses,
  pillClasses,
  sharedClasses,
  toneColors,
} from './EntityTagHistory.styles';
import type {
  ActorLabelProps,
  DirectionGlyphProps,
  EventTimestampProps,
  PillProps,
  TagStateLabelProps,
} from './entityTagHistory.types';

/** ▲ = added / included, ▼ = removed / excluded, ● = unknown. */
export function DirectionGlyph({
  direction = 'none',
  color,
  size = 9,
}: DirectionGlyphProps) {
  return (
    <span aria-hidden className={directionGlyphClasses.root(direction, color, size)} />
  );
}

export function Pill({ tone, children, title, direction }: PillProps) {
  const colors = toneColors[tone];
  return (
    <span title={title} className={pillClasses.root(tone, !!title)}>
      {direction && direction !== 'none' ? (
        <DirectionGlyph direction={direction} color={colors.solid} size={8} />
      ) : (
        <span aria-hidden className={pillClasses.dot(tone)} />
      )}
      {children}
    </span>
  );
}

export function TagStateLabel({ tagged }: TagStateLabelProps) {
  if (tagged === undefined) {
    return <span className={sharedClasses.mutedText}>Not provided</span>;
  }
  return (
    <Pill
      tone={tagged ? 'success' : 'critical'}
      title={
        tagged
          ? 'A tag check for this entity returned true from this point on.'
          : 'A tag check for this entity returned false from this point on.'
      }
    >
      {tagged ? 'Tagged' : 'Not tagged'}
    </Pill>
  );
}

export function ActorLabel({ actorId, actorNames }: ActorLabelProps) {
  if (!actorId) {
    return <span className={sharedClasses.mutedText}>Not provided</span>;
  }
  const actor = actorNames[actorId];
  const name = actor?.name;
  const display = name ?? actorId;
  return (
    <span title={actor?.username || actorId} className={actorLabelClasses.root}>
      <span aria-hidden className={actorLabelClasses.avatar(!!name)}>
        {name ? name.charAt(0) : '?'}
      </span>
      <div className={actorLabelClasses.name(!!name)}>{display}</div>
    </span>
  );
}

export function EventTimestamp({ value }: EventTimestampProps) {
  const date = value ? new Date(value) : undefined;
  if (!value || !date || Number.isNaN(date.getTime())) {
    return <span className={sharedClasses.mutedText}>Not provided</span>;
  }
  return (
    <time dateTime={value} title={value} className={eventTimestampClasses.root}>
      <span className={eventTimestampClasses.date}>{format(date, 'MMM d, yyyy')}</span>
      <span className={eventTimestampClasses.time}>{format(date, 'h:mm:ss a')}</span>
    </time>
  );
}
